﻿import { useRouter } from "@tanstack/react-router";
import {
  createContext,
  type ReactNode,
  useCallback,
  use,
  useMemo,
  useRef,
  useState,
  type JSX,
} from "react";

export interface UnsavedChangesDrawerContextProps {
  showUnsavedChangesDrawer: (onSave: () => Promise<void>) => void;
  closeUnsavedChangesDrawer: () => void;
  isDrawerVisible: boolean;
  isSaving: boolean;
  handleSaveAndLeave: () => Promise<void>;
  handleLeaveWithoutSaving: () => void;
  setRemoveBlockerCallback: (callback: () => void) => void;
}

export const UnsavedChangesDrawerContext = createContext<
  UnsavedChangesDrawerContextProps | undefined
>(undefined);

interface UnsavedChangesDrawerProviderProps {
  children: ReactNode;
}

export const UnsavedChangesDrawerProvider = ({
  children,
}: UnsavedChangesDrawerProviderProps): JSX.Element => {
  const router = useRouter();

  const [isSaving, setIsSaving] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const saveCallbackRef = useRef<(() => Promise<void>) | null>(null);
  const pendingNavigationRef = useRef<(() => void) | null>(null);
  const removeBlockerCallbackRef = useRef<(() => void) | null>(null);

  const setRemoveBlockerCallback = useCallback((callback: () => void) => {
    removeBlockerCallbackRef.current = callback;
  }, []);

  const showUnsavedChangesDrawer = useCallback((onSave: () => Promise<void>): void => {
    saveCallbackRef.current = onSave;
    setIsDrawerOpen(true);
  }, []);

  const closeUnsavedChangesDrawer = useCallback(() => {
    setIsDrawerOpen(false);
    saveCallbackRef.current = null;
    pendingNavigationRef.current = null;
  }, []);

  const handleSaveAndLeave = useCallback(async () => {
    const saveCallback = saveCallbackRef.current;
    if (!saveCallback) return;

    setIsSaving(true);
    try {
      await saveCallback();
      closeUnsavedChangesDrawer();

      // Remove o blocker antes de navegar
      if (removeBlockerCallbackRef.current) {
        removeBlockerCallbackRef.current();
      }

      if (pendingNavigationRef.current) {
        pendingNavigationRef.current();
        pendingNavigationRef.current = null;
      } else {
        router.history.back();
      }
    } catch (error) {
      // eslint-disable-next-line no-console -- Necessário para debug de erros ao salvar
      console.error("Error saving:", error);
    } finally {
      setIsSaving(false);
    }
  }, [closeUnsavedChangesDrawer, router.history]);

  const handleLeaveWithoutSaving = useCallback(() => {
    closeUnsavedChangesDrawer();

    // Remove o blocker antes de navegar
    if (removeBlockerCallbackRef.current) {
      removeBlockerCallbackRef.current();
    }

    if (pendingNavigationRef.current) {
      pendingNavigationRef.current();
      pendingNavigationRef.current = null;
    } else {
      router.history.back();
    }
  }, [closeUnsavedChangesDrawer, router.history]);

  const isDrawerVisible = isDrawerOpen;

  const contextValue = useMemo(
    () => ({
      showUnsavedChangesDrawer,
      closeUnsavedChangesDrawer,
      isDrawerVisible,
      isSaving,
      handleSaveAndLeave,
      handleLeaveWithoutSaving,
      setRemoveBlockerCallback,
    }),
    [
      showUnsavedChangesDrawer,
      closeUnsavedChangesDrawer,
      isDrawerVisible,
      isSaving,
      handleSaveAndLeave,
      handleLeaveWithoutSaving,
      setRemoveBlockerCallback,
    ],
  );

  return (
    <UnsavedChangesDrawerContext value={contextValue}>
      {children}
    </UnsavedChangesDrawerContext>
  );
};

// eslint-disable-next-line react-refresh/only-export-components -- Hook consumidor do context deve estar no mesmo arquivo
export const useUnsavedChangesDrawer = (): UnsavedChangesDrawerContextProps => {
  const context = use(UnsavedChangesDrawerContext);
  if (!context) {
    throw new Error(
      "useUnsavedChangesDrawer must be used within a UnsavedChangesDrawerProvider",
    );
  }
  return context;
};
